/* istanbul ignore file */
import React from 'react';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { TextField, EnumField, DateField, BooleanField } from 'framework/ui/field';
import { INVOICE_STATUSES_OPTIONS, INVOICE_STATUSES } from 'domain/finance/invoice/useInvoiceService';
import InvoiceHeader from 'domain/finance/invoice/shared/InvoiceHeader';
import RejectInvoiceButton from 'domain/finance/invoice/shared/button/RejectInvoiceButton';
import MarkAsPaidInvoiceButton from 'domain/finance/invoice/shared/button/MarkAsPaidInvoiceButton';
import CopyInvoiceButton from 'domain/finance/invoice/shared/button/CopyInvoiceButton';
import ApproveInvoiceButton from 'domain/finance/invoice/shared/button/ApproveInvoiceButton';
import CancelPaymentInvoiceButton from 'domain/finance/invoice/shared/button/CancelPaymentInvoiceButton';
import EditInvoiceButton from 'domain/finance/invoice/shared/button/EditInvoiceButton';
import SingleInvoiceBreadcrumbs from 'domain/finance/invoice/shared/SingleInvoiceBreadcrumbs';
import InvoiceAttachmentsListField from 'domain/finance/invoice/shared/field/InvoiceAttachmentsListField';
import { OfficeCodeField } from 'domain/shared/employee';
import { LedgerLink } from 'domain/shared/ledger';
import TransfersListField from 'domain/finance/shared/field/TransfersListField';
import { BUSINESS_OBJECT_TYPES } from 'domain/finance/ledger/useLedgerService';
import { useInvoiceId } from '../shared/useInvoiceRouting';
import LinkInvoiceToPurchaseOrderButton from 'domain/finance/purchaseOrder/shared/button/LinkInvoiceToPurchaseOrderButton';
import PurchaseOrderLink from 'domain/finance/purchaseOrder/shared/field/PurchaseOrderLink';
import { GOODS_AND_SERVICES_DELIVERY_STATUS_OPTIONS } from 'domain/finance/purchaseOrder/usePurchaseOrderService';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import MarkAsDeliveredGoodsAndServicesButton from '../shared/button/MarkAsDeliveredGoodsAndServicesButton';
import { useShowContext } from 'framework/show';
import InvoiceReferencesList from '../shared/field/InvoiceReferencesList';
import VendorLink from 'domain/finance/vendors/shared/fields/VendorLink';

const ViewInvoicePageContent = ({
  isSensitiveDataLoading = false,
  isSensitiveDataIsShown = false,
  reloadSensitiveData,
  toggleSensitiveDataButton = <></>,
}: {
  isSensitiveDataLoading?: boolean;
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataButton?: React.ReactElement;
  reloadSensitiveData?: () => void;
}) => {
  const id = useInvoiceId();
  const { setData = () => {} } = useShowContext();

  return (
    <ShowPageLayout
      entityHeader={<InvoiceHeader />}
      breadcrumbs={<SingleInvoiceBreadcrumbs />}
      headerButtons={[
        toggleSensitiveDataButton,
        <MarkAsDeliveredGoodsAndServicesButton
          futureExpenseSource="isFutureExpense"
          invoiceIdSource="id"
          deliveryStatusSource="goodsAndServices.deliveryStatus"
          invoiceStatusSource="status"
          afterSubmit={{
            execute: (response: any) => {
              if (isSensitiveDataIsShown && reloadSensitiveData) {
                reloadSensitiveData();
              }
              setData({
                ...response,
              });
            },
          }}
        />,
        <EditInvoiceButton
          isVisible={(data: any) => data.isInvoiceDocumentWillArriveLater && data.status !== INVOICE_STATUSES.REJECTED}
        />,
        <CopyInvoiceButton />,
        <LinkInvoiceToPurchaseOrderButton statusSource="status" officeSource="office" />,
        <ApproveInvoiceButton statusSource="status" />,
        <MarkAsPaidInvoiceButton isSensitiveDataIsShown={isSensitiveDataIsShown} statusSource="status" />,
        <CancelPaymentInvoiceButton statusSource="status" />,
        <RejectInvoiceButton statusSource="status" />,
      ]}
      hasSeparateSections
    >
      <ShowSection title="Main information">
        <SectionRow>
          <TextField label="Invoice number" source="invoiceNumber" />
          <DateField label="Date" source="invoiceDate" />
          <OfficeCodeField source="office" label="Office" />
        </SectionRow>
        <SectionRow>
          <TextField isVisible={(data: any) => !data.getValueBySource('vendorId')} label="Name" source="name" />
          <VendorLink
            isVisible={(data: any) => data.getValueBySource('vendorId')}
            label="Vendor"
            source="vendorId"
            nameSource="vendorName"
          />
          <EnumField label="Status" source="status" options={INVOICE_STATUSES_OPTIONS} />
          <TextField
            label="Reject reason"
            source="rejectReason"
            isVisible={(data: any) => data.getValueBySource('status') === INVOICE_STATUSES.REJECTED}
          />
          <BooleanField source="isPurchaseClassifiedAsAsset" label="Is purchase classified as asset?" />
        </SectionRow>
        <SectionRow>
          <InvoiceTotalField
            label="Total"
            source="subTotal.amount"
            currencySource="subTotal.currency"
            isVisible={isSensitiveDataIsShown}
          />
          <InvoiceTotalField
            label="Paid amount"
            source="invoicePaidSubTotal.amount"
            currencySource="invoicePaidSubTotal.currency"
            isVisible={isSensitiveDataIsShown}
          />
          <BooleanField source="isFutureExpense" label="Is future expense?" />
          <LedgerLink source="account" nameSource="account" label="Account" />
          <TextField source="budgetCategory" nameSource="budgetCategory" label="Budget category" />
          <PurchaseOrderLink
            source="linkedPurchaseOrder.id"
            nameSource="linkedPurchaseOrder.title"
            label="Purchase order"
          />
        </SectionRow>
        <BigSectionRow>
          <TextField label="Description" source="description" />
        </BigSectionRow>
        <SectionRow>
          <InvoiceAttachmentsListField source="invoiceDocumentId" label="Document" isOneArtifact />
        </SectionRow>
      </ShowSection>
      <ShowSection title="Requisites">
        <SectionRow>
          <TextField source="requisites.bankName" label="Bank name" />
          <TextField source="requisites.bankAccount" label="Bank account" />
          <TextField source="requisites.recipient" label="Recipient" />
        </SectionRow>
      </ShowSection>
      <ShowSection title="Goods and services">
        <SectionRow>
          <EnumField
            label="Delivery status"
            source="goodsAndServices.deliveryStatus"
            options={GOODS_AND_SERVICES_DELIVERY_STATUS_OPTIONS}
          />
          <DateField label="Delivery date" source="goodsAndServices.deliveryDate" />
          <InvoiceTotalField
            label="Delivered amount"
            source="invoiceDeliveredSubTotal.amount"
            currencySource="invoiceDeliveredSubTotal.currency"
            width="25%"
            isVisible={isSensitiveDataIsShown || isSensitiveDataLoading}
          />
        </SectionRow>
      </ShowSection>
      <ShowSection isVisible={isSensitiveDataIsShown || isSensitiveDataLoading}>
        <TransfersListField
          businessObjectId={id}
          businessObjectType={BUSINESS_OBJECT_TYPES.INVOICE}
          startDateSource="invoiceDate"
          endDateSource="invoicePaidDate"
        />
      </ShowSection>
      <ShowSection title="References">
        <InvoiceReferencesList source="references" />
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewInvoicePageContent;
